import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { baseURL } from "services/API";

const utilitiesBase = `${baseURL}/admin/utilities`;

const utilitiesErrorMessage = (error, fallback) => {
	const data = error?.response?.data;
	if (!data) return error?.message || fallback;
	if (typeof data === "string") return data;
	if (data.error) return typeof data.error === "string" ? data.error : fallback;
	if (data.message) return data.message;
	if (data.errors?.utilities) {
		const nested = data.errors.utilities;
		if (typeof nested === "string") return nested;
		const firstKey = Object.keys(nested)[0];
		const val = nested[firstKey];
		if (Array.isArray(val)) return val[0];
		if (typeof val === "string") return val;
	}
	return fallback;
};

export const emptyUtilitiesForm = () => ({
	enabled: false,
	provider_account_number: "",
	electricity_enabled: true,
	water_enabled: true,
	notes: "",
});

export const utilitiesFormFromApi = (utilities) => {
	if (!utilities || utilities.enabled === false) {
		return {
			...emptyUtilitiesForm(),
			enabled: Boolean(utilities?.enabled),
			provider_account_number: utilities?.provider_account_number || "",
			electricity_enabled: utilities?.electricity_enabled !== false,
			water_enabled: utilities?.water_enabled !== false,
			notes: utilities?.notes || "",
		};
	}
	return {
		enabled: true,
		provider_account_number: utilities.provider_account_number || "",
		electricity_enabled: utilities.electricity_enabled !== false,
		water_enabled: utilities.water_enabled !== false,
		notes: utilities.notes || "",
	};
};

/** Build nested `utilities` for unit create/edit. Returns null to omit from payload. */
export const buildUtilitiesPayload = (form, { mode = "create", initialUtilities = null } = {}) => {
	if (form?.enabled) {
		if (!form.provider_account_number?.trim()) {
			return { error: "Select a provider account number to enable utilities." };
		}
		return {
			payload: {
				enabled: true,
				provider_account_number: String(form.provider_account_number).trim(),
				electricity_enabled: form.electricity_enabled !== false,
				water_enabled: form.water_enabled !== false,
				notes: form.notes || "",
			},
		};
	}
	if (mode === "edit" && initialUtilities?.enabled) {
		return { payload: { enabled: false } };
	}
	return { payload: null };
};

export const useUtilitiesStatus = () => {
	const token = useSelector((state) => state.token);
	const [status, setStatus] = useState(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!token) return;
		axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
		let cancelled = false;
		const load = async () => {
			setLoading(true);
			try {
				const response = await axios.get(`${utilitiesBase}/status`);
				if (!cancelled) setStatus(response.data);
			} catch {
				if (!cancelled) setStatus({ configured: true });
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		load();
		return () => {
			cancelled = true;
		};
	}, [token]);

	return { status, loading, configured: status?.configured !== false };
};

export const usePartnerUnits = ({ enabled = false, unmappedOnly = true } = {}) => {
	const token = useSelector((state) => state.token);
	const [units, setUnits] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const refetch = useCallback(async () => {
		if (!token || !enabled) {
			setUnits([]);
			return;
		}
		axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
		setLoading(true);
		setError(null);
		try {
			const response = await axios.get(`${utilitiesBase}/partner-units`, {
				params: { unmapped_only: unmappedOnly },
			});
			setUnits(response.data?.units ?? []);
		} catch (err) {
			setError(utilitiesErrorMessage(err, "Failed to load partner utility accounts."));
			setUnits([]);
		} finally {
			setLoading(false);
		}
	}, [token, enabled, unmappedOnly]);

	useEffect(() => {
		refetch();
	}, [refetch]);

	return { units, loading, error, refetch };
};

export const useTenantUtilitiesStatus = (tenantId, occupancyId, { includeBalance = true } = {}) => {
	const token = useSelector((state) => state.token);
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	useEffect(() => {
		if (!tenantId || !occupancyId || !token) {
			setData(null);
			setLoading(false);
			return;
		}
		axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
		let cancelled = false;
		const load = async () => {
			setLoading(true);
			setError(null);
			try {
				const response = await axios.get(`${utilitiesBase}/tenant-status`, {
					params: {
						tenant_id: tenantId,
						occupancy_id: occupancyId,
						include_balance: includeBalance,
					},
				});
				if (!cancelled) setData(response.data?.data ?? response.data);
			} catch (err) {
				if (!cancelled) {
					setError(utilitiesErrorMessage(err, "Failed to load utilities status."));
					setData(null);
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		load();
		return () => {
			cancelled = true;
		};
	}, [tenantId, occupancyId, token, includeBalance]);

	return { data, loading, error };
};

export { utilitiesErrorMessage };
